import { useCallback, useLayoutEffect, useRef } from 'react';
import type { DMChatMessage } from '../types/dm';

interface UseMessageListScrollParams {
  messages: DMChatMessage[] | undefined;
  hasMore: boolean;
  onLoadMore: () => void | Promise<void>;
  onAtBottomChange: (isAtBottom: boolean) => void;
  enableInitialScrollToBottom?: boolean;
}

interface UseMessageListScrollResult {
  listContainerRef: React.RefObject<HTMLDivElement>;
  handleScroll: () => void;
  scrollToBottom: () => void;
}

export function useMessageListScroll({
  messages,
  hasMore,
  onLoadMore,
  onAtBottomChange,
  enableInitialScrollToBottom = true
}: UseMessageListScrollParams): UseMessageListScrollResult {
  const messageCount = messages?.length ?? 0;
  const listContainerRef = useRef<HTMLDivElement>(null);
  const isLoadingMoreRef = useRef<boolean>(false);
  const didInitialScrollRef = useRef<boolean>(false);
  const prevScrollHeightRef = useRef<number>(0);
  const prevScrollTopRef = useRef<number>(0);

  const TOP_THRESHOLD_PX = 32;
  const BOTTOM_THRESHOLD_PX = 16;

  const scrollToBottom = useCallback(() => {
    const el = listContainerRef.current;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
  }, []);

  const handleScroll = useCallback(() => {
    const el = listContainerRef.current;
    if (!el) return;

    const isAtTop = el.scrollTop <= TOP_THRESHOLD_PX;
    const isAtBottom = el.scrollHeight - (el.scrollTop + el.clientHeight) <= BOTTOM_THRESHOLD_PX;

    onAtBottomChange(isAtBottom);

    if (isAtTop && hasMore && !isLoadingMoreRef.current) {
      isLoadingMoreRef.current = true;
      prevScrollHeightRef.current = el.scrollHeight;
      prevScrollTopRef.current = el.scrollTop;
      Promise.resolve(onLoadMore()).catch(() => {
        isLoadingMoreRef.current = false;
      });
    }
  }, [hasMore, onAtBottomChange, onLoadMore]);

  // 최초 접속 시에만 하단으로 스크롤
  useLayoutEffect(() => {
    if (!enableInitialScrollToBottom) return;
    const el = listContainerRef.current;
    if (!el) return;
    if (didInitialScrollRef.current) return;
    if (messageCount === 0) return;

    const scrollBottom = () => {
      el.scrollTop = el.scrollHeight;
      onAtBottomChange(true);
    };

    didInitialScrollRef.current = true;
    scrollBottom();
    const rafId = requestAnimationFrame(scrollBottom);
    const t1 = setTimeout(scrollBottom, 0);
    const t2 = setTimeout(scrollBottom, 120);

    return () => {
      cancelAnimationFrame(rafId);
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [enableInitialScrollToBottom, messageCount, onAtBottomChange]);

  // 이전 페이지 로딩 후 스크롤 위치 보정
  useLayoutEffect(() => {
    if (!isLoadingMoreRef.current) return;
    if (messageCount === 0) {
      isLoadingMoreRef.current = false;
      return;
    }
    const el = listContainerRef.current;
    if (!el) {
      isLoadingMoreRef.current = false;
      return;
    }
    const delta = el.scrollHeight - prevScrollHeightRef.current;
    if (delta !== 0) {
      el.scrollTop = prevScrollTopRef.current + delta;
    }
    isLoadingMoreRef.current = false;
  }, [messageCount]);

  return {
    listContainerRef,
    handleScroll,
    scrollToBottom
  };
}
